/**
 * IconTargetBadge - Tiered target badge icons for incentive progress displays
 * Three tiers with increasing color intensity (light → medium → dark blue).
 */

import colors from "../../scss/colors.module.scss";

export type TargetBadgeTier = "tier1" | "tier2" | "tier3";

type IconTargetBadgeProps = {
  tier: TargetBadgeTier;
  size?: number;
};

const TIER_COLORS: Record<TargetBadgeTier, string> = {
  tier1: "#6677E9", // Light blue (SSI-specific, no design token yet)
  tier2: colors.blue, // $blue from design tokens
  tier3: "#0E1934", // Dark blue (SSI-specific, no design token yet)
};

// ViewBox is 24x24 with 15x15 inner icon (ratio 0.625)
// This allows CSS scaling to achieve: 24px→15px, 20px→12.5px, 16px→10px inner icon
const ICON_SCALE = 15 / 14; // Scale original 14x14 path to 15x15
const ICON_OFFSET = (24 - 15) / 2; // Center 15x15 icon in 24x24 viewBox

export const IconTargetBadge = ({ tier, size = 24 }: IconTargetBadgeProps) => {
  const fillColor = TIER_COLORS[tier];

  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="12" fill={fillColor} />
      <path
        fill="white"
        transform={`translate(${ICON_OFFSET}, ${ICON_OFFSET}) scale(${ICON_SCALE})`}
        d="M0.109375 1.06641C0.0273438 0.957031 0 0.820312 0 0.683594C0 0.300781 0.300781 0 0.65625 0H3.85547C4.18359 0 4.45703 0.164062 4.62109 0.4375L6.31641 3.25391L6.48047 3.52734C6.01562 3.58203 5.55078 3.66406 5.11328 3.82812L3.60938 1.3125H1.85938L3.91016 4.40234C3.55469 4.64844 3.19922 4.92188 2.89844 5.25L0.109375 1.06641ZM7.65625 3.25391L9.35156 0.4375C9.51562 0.164062 9.78906 0 10.1172 0H13.3164C13.6992 0 14 0.300781 14 0.683594C14 0.820312 13.9453 0.957031 13.8633 1.06641L11.0742 5.25C10.7734 4.92188 10.4453 4.64844 10.0625 4.40234L12.1133 1.3125H10.3633L8.85938 3.82812C8.42188 3.66406 7.95703 3.58203 7.49219 3.52734L7.65625 3.25391ZM7 5.6875C5.74219 5.6875 4.59375 6.37109 3.96484 7.4375C3.33594 8.53125 3.33594 9.87109 3.96484 10.9375C4.59375 12.0312 5.74219 12.6875 7 12.6875C8.23047 12.6875 9.37891 12.0312 10.0078 10.9375C10.6367 9.87109 10.6367 8.53125 10.0078 7.4375C9.37891 6.37109 8.23047 5.6875 7 5.6875ZM7 14C5.27734 14 3.69141 13.0977 2.81641 11.5938C1.96875 10.1172 1.96875 8.28516 2.81641 6.78125C3.69141 5.30469 5.27734 4.375 7 4.375C8.69531 4.375 10.2812 5.30469 11.1562 6.78125C12.0039 8.28516 12.0039 10.1172 11.1562 11.5938C10.2812 13.0977 8.69531 14 7 14ZM7.19141 6.97266L7.71094 8.03906C7.73828 8.09375 7.79297 8.14844 7.875 8.14844L9.05078 8.33984C9.21484 8.33984 9.29688 8.58594 9.16016 8.69531L8.3125 9.51562C8.25781 9.57031 8.25781 9.65234 8.25781 9.70703L8.44922 10.8828C8.47656 11.0742 8.3125 11.2109 8.14844 11.1289L7.08203 10.5547C7.02734 10.5273 6.94531 10.5273 6.89062 10.5547L5.82422 11.1016C5.6875 11.2109 5.49609 11.0742 5.52344 10.8828L5.71484 9.70703C5.74219 9.65234 5.71484 9.57031 5.66016 9.51562L4.8125 8.69531C4.67578 8.55859 4.75781 8.33984 4.92188 8.3125L6.09766 8.14844C6.17969 8.14844 6.23438 8.09375 6.26172 8.03906L6.78125 6.97266C6.86328 6.80859 7.10938 6.80859 7.19141 6.97266Z"
      />
    </svg>
  );
};
